import { makeAutoObservable, runInAction } from "mobx";
import { isAxiosError } from "@/api";
import {
  FileUploadConfig,
  isFileExceedsMaxSizeError,
  isUnsupportedFilesError,
  validateFiles,
} from "@/fileUpload/fileUploadDomain";
import { TMessage, TMessageParams } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { TransactionType } from "@/types/enums/TransactionType";
import {
  IssueNewSharesDTO,
  IssueShareTypeDTO,
  NewTransaction,
  TransactionAllShares,
} from "@/types/Transactions";
import { AgenticApiClient, AgenticAskAttachment, AgenticAskContext } from "./agenticApiClient";
import { logDebug } from "@/utils/log";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";

export const agenticUploadConfig: FileUploadConfig = {
  supportedFileTypes: [
    { extensions: ["pdf"], magicBytes: [] },
    { extensions: ["png"], magicBytes: [[137, 80, 78, 71, 13, 10, 26, 10]] },
  ],
  maxFileSize: 10 * 1024 * 1024,
  maxRequestSize: 10 * 1024 * 1024,
};
import { isProductionEnvironment } from "@/utils/environments.ts";

export type AgenticChatRole = "user" | "assistant";

export type AgenticProgressState = "running" | "done" | "error";

export type AgenticToolCallSubStep = {
  step: string;
  message: string;
};

export type AgenticToolCall = {
  id: string;
  name: string;
  error: string | null;
  state: AgenticProgressState;
  count: number;
  serverLabel: string;
  labelKey?: TMessageKey;
  labelParams?: TMessageParams;
  subSteps: AgenticToolCallSubStep[];
};

export type AgenticIssueDraftPayload = IssueNewSharesDTO;

type AgenticDraftTransactionBase = {
  source: string;
  toolCallId: string;
  rawPayload?: unknown;
  error: string | null;
};

export type AgenticIssueDraftTransaction = AgenticDraftTransactionBase & {
  transactionType: TransactionType.ISSUE;
  payload: AgenticIssueDraftPayload | null;
};

export type AgenticBuySellDraftTransaction = AgenticDraftTransactionBase & {
  transactionType: TransactionType.BUY_SELL;
  payload: NewTransaction | TransactionAllShares | null;
};

export type AgenticDraftTransaction = AgenticIssueDraftTransaction | AgenticBuySellDraftTransaction;

export type AgenticDecisionSuggestion = {
  decisionType: DecisionType;
  title?: string;
};

export type AgenticDocumentSuggestion = {
  documentType: DocumentType;
};

export type AgenticDecisionSuggestionResult = {
  decisions?: AgenticDecisionSuggestion[];
  documents?: AgenticDocumentSuggestion[];
};

export type AgenticChatMessage = {
  role: AgenticChatRole;
  content: string;
  toolCalls?: AgenticToolCall[];
  draftTransaction?: AgenticDraftTransaction;
};

export class AgenticTransactionChatModel {
  private readonly apiClient: AgenticApiClient;
  private context?: AgenticAskContext;
  messages: AgenticChatMessage[] = [];
  attachments: AgenticAskAttachment[] = [];
  currentInput = "";
  isStreaming = false;
  streamingText = "";
  error: TMessage | null = null;
  private abortController: AbortController | null = null;
  private pendingToolCalls: AgenticToolCall[] = [];
  private pendingStreamingEvents: AgenticToolCall[] = [];
  private pendingDraftTransaction: AgenticDraftTransaction | null = null;

  constructor(
    apiClient: AgenticApiClient,
    context?: AgenticAskContext,
    readonly options?: {
      onDecisionSuggestions?: (suggestions: AgenticDecisionSuggestionResult) => void;
    },
  ) {
    this.apiClient = apiClient;
    this.context = context;
    makeAutoObservable(this, { sendMessage: false }, { autoBind: true });
  }

  setContext(context?: AgenticAskContext) {
    this.context = context;
  }

  setInput(value: string) {
    this.currentInput = value;
  }

  get canSend(): boolean {
    return (
      (this.currentInput.trim().length > 0 || this.attachments.length > 0) && !this.isStreaming
    );
  }

  get streamingToolCalls(): AgenticToolCall[] {
    return this.pendingStreamingEvents.slice();
  }

  isAskAvailable(signal: AbortSignal): Promise<boolean> {
    if (isProductionEnvironment()) {
      return Promise.resolve(false);
    }
    return this.apiClient.isAskAvailable(signal);
  }

  async selectFiles(files: File[], fileUploadConfig: FileUploadConfig) {
    if (!files.length) {
      return;
    }

    try {
      const validFiles = await validateFiles(files, fileUploadConfig);
      const attachments = await Promise.all(validFiles.map(fileToAttachment));

      runInAction(() => {
        this.attachments = attachments;
        this.error = null;
      });
    } catch (error) {
      runInAction(() => {
        if (isUnsupportedFilesError(error)) {
          this.error = { translated: "Unsupported file type. Please upload PDF or PNG." };
        } else if (isFileExceedsMaxSizeError(error)) {
          this.error = { translated: "File is too large." };
        } else {
          this.error = { translated: "Unable to process selected file." };
        }
      });
    }
  }

  removeAttachment(index: number) {
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  async sendMessage() {
    const prompt = this.currentInput.trim();
    const attachments = [...this.attachments];
    if ((!prompt && attachments.length === 0) || this.isStreaming) return;

    const attachmentSummary =
      attachments.length > 0
        ? `[${attachments.map((attachment) => attachment.filename).join(", ")}]`
        : undefined;
    const userMessageContent = prompt
      ? attachmentSummary
        ? `${prompt}\n${attachmentSummary}`
        : prompt
      : attachmentSummary || "";

    const conversation = this.messages.slice(-20).map((message) => ({
      role: message.role,
      content: message.content,
    }));

    runInAction(() => {
      this.messages.push({ role: "user", content: userMessageContent });
      this.currentInput = "";
      this.attachments = [];
      this.isStreaming = true;
      this.streamingText = "";
      this.error = null;
      this.pendingToolCalls = [];
      this.pendingStreamingEvents = [];
      this.pendingDraftTransaction = null;
    });

    this.abortController = new AbortController();

    try {
      await this.apiClient.ask(
        { prompt, conversation, context: this.context, attachments },
        {
          signal: this.abortController.signal,
          onSseEvent: (event, data) => {
            this.handleSSEEvent(event, data);
          },
        },
      );

      runInAction(() => {
        if (this.streamingText) {
          this.messages.push({
            role: "assistant",
            content: this.streamingText,
            toolCalls: this.pendingToolCalls.length > 0 ? [...this.pendingToolCalls] : undefined,
            draftTransaction: this.pendingDraftTransaction ?? undefined,
          });
          this.streamingText = "";
        }
        this.isStreaming = false;
      });
    } catch (e) {
      if (isAxiosError(e) && e.code === "ERR_CANCELED") {
        return;
      }
      runInAction(() => {
        if (isAxiosError(e)) {
          const status = e.response?.status;
          const responseText = typeof e.response?.data === "string" ? e.response.data : e.message;
          this.error = {
            translated: status ? `Error: ${status} ${responseText}` : responseText,
          };
        } else {
          this.error = { translated: e instanceof Error ? e.message : "Unknown error" };
        }
        this.isStreaming = false;
      });
    } finally {
      this.abortController = null;
    }
  }

  abort() {
    this.abortController?.abort();
    this.isStreaming = false;
  }

  private handleSSEEvent(event: string, dataStr: string) {
    try {
      const data = JSON.parse(dataStr) as unknown;
      logDebug("handleSSEEvent", event, dataStr);
      switch (event) {
        case "status":
          {
            const statusEvent = parseStatusStreamingEvent(data);
            if (statusEvent) {
              runInAction(() => {
                this.pendingStreamingEvents = upsertToolCall(
                  this.pendingStreamingEvents,
                  statusEvent,
                );
              });
            }
          }
          break;

        case "openai_event":
          {
            const openAiEvent = parseOpenAIStreamingEvent(data);
            if (openAiEvent) {
              const incrementCount =
                openAiEvent.id === "openai:mcp_call_state" && openAiEvent.state === "running";

              runInAction(() => {
                this.pendingStreamingEvents = upsertToolCall(
                  this.pendingStreamingEvents,
                  openAiEvent,
                  { incrementCount },
                );
              });
            }
          }
          break;

        case "message_delta":
          runInAction(() => {
            const messageDelta = parseMessageDelta(data);
            if (messageDelta) {
              this.streamingText += messageDelta;
            }
          });
          break;

        case "tool_call":
          {
            const toolCall = parseToolCall(data);
            const draftTransaction = parseAgenticDraftTransactionFromToolCall(
              data,
              this.context?.orgNo,
            );

            runInAction(() => {
              if (toolCall) {
                this.pendingToolCalls = upsertToolCall(this.pendingToolCalls, toolCall);
                this.pendingStreamingEvents = upsertToolCall(
                  this.pendingStreamingEvents,
                  toStreamingEventFromToolCall(toolCall),
                );
              }

              if (draftTransaction) {
                this.pendingDraftTransaction = mergeDraftTransaction(
                  this.pendingDraftTransaction,
                  draftTransaction,
                );
              }
            });
          }
          break;

        case "draft_transaction":
          {
            const draftTransaction = parseAgenticDraftTransaction(data, this.context?.orgNo);
            if (draftTransaction) {
              runInAction(() => {
                this.pendingDraftTransaction = mergeDraftTransaction(
                  this.pendingDraftTransaction,
                  draftTransaction,
                );
              });
            }
          }
          break;

        case "decision_suggestions":
          this.options?.onDecisionSuggestions?.(data as AgenticDecisionSuggestionResult);
          break;

        case "message":
          runInAction(() => {
            const messageResponse = parseMessageResponse(data);
            if (messageResponse) {
              this.streamingText = messageResponse;
            }
          });
          break;

        case "tool_progress":
          {
            const progress = parseToolProgress(data);
            if (progress) {
              runInAction(() => {
                const target = this.pendingStreamingEvents.find(
                  (tc) => tc.state === "running" && tc.id === "openai:mcp_call_state",
                );
                if (target) {
                  target.subSteps = [...target.subSteps, progress];
                }
              });
            }
          }
          break;

        case "done":
          break;

        case "error":
          runInAction(() => {
            const errorMessage = parseErrorMessage(data) ?? "Unknown error from AI service";
            this.error = {
              translated: errorMessage,
            };
            this.pendingStreamingEvents = upsertToolCall(this.pendingStreamingEvents, {
              id: "status:error",
              name: "error",
              error: errorMessage,
              state: "error",
              count: 1,
              serverLabel: "",
              labelKey: "agentic.chat.progress.ai-service-error",
              subSteps: [],
            });
          });
          break;
      }
    } catch {
      // non-JSON data lines are ignored
    }
  }
}

function hasDraftPayload(
  draftTransaction: AgenticDraftTransaction | null | undefined,
): draftTransaction is AgenticDraftTransaction {
  return (
    !!draftTransaction &&
    draftTransaction.payload !== null &&
    draftTransaction.payload !== undefined
  );
}

function mergeDraftTransaction(
  currentDraftTransaction: AgenticDraftTransaction | null,
  nextDraftTransaction: AgenticDraftTransaction,
): AgenticDraftTransaction {
  if (
    !hasDraftPayload(currentDraftTransaction) ||
    nextDraftTransaction.error ||
    nextDraftTransaction.payload !== null ||
    currentDraftTransaction.transactionType !== nextDraftTransaction.transactionType
  ) {
    return nextDraftTransaction;
  }

  if (
    nextDraftTransaction.transactionType === TransactionType.ISSUE &&
    currentDraftTransaction.transactionType === TransactionType.ISSUE
  ) {
    return {
      ...nextDraftTransaction,
      payload: currentDraftTransaction.payload,
    };
  }

  if (
    nextDraftTransaction.transactionType === TransactionType.BUY_SELL &&
    currentDraftTransaction.transactionType === TransactionType.BUY_SELL
  ) {
    return {
      ...nextDraftTransaction,
      payload: currentDraftTransaction.payload,
    };
  }

  return nextDraftTransaction;
}

type UpsertToolCallOptions = {
  incrementCount?: boolean;
};

function upsertToolCall(
  currentToolCalls: AgenticToolCall[],
  nextToolCall: AgenticToolCall,
  options: UpsertToolCallOptions = {},
): AgenticToolCall[] {
  const existingIndex = currentToolCalls.findIndex((toolCall) => toolCall.id === nextToolCall.id);
  if (existingIndex === -1) {
    return [...currentToolCalls, { ...nextToolCall, count: nextToolCall.count || 1 }];
  }

  const existingToolCall = currentToolCalls[existingIndex];
  const existingCount = existingToolCall.count || 1;
  const nextCount = nextToolCall.count || 1;
  const mergedCount = options.incrementCount
    ? existingCount + 1
    : Math.max(existingCount, nextCount);
  const mergedSubSteps =
    nextToolCall.subSteps.length > 0 ? nextToolCall.subSteps : existingToolCall.subSteps;
  const mergedToolCall: AgenticToolCall = {
    ...existingToolCall,
    ...nextToolCall,
    count: mergedCount,
    serverLabel: nextToolCall.serverLabel || existingToolCall.serverLabel,
    subSteps: mergedSubSteps,
  };

  const updatedToolCalls = [...currentToolCalls];
  updatedToolCalls[existingIndex] = mergedToolCall;
  return updatedToolCalls;
}

function toStreamingEventFromToolCall(toolCall: AgenticToolCall): AgenticToolCall {
  return {
    id: `tool_call:${toolCall.id}`,
    name: toolCall.name,
    error: toolCall.error,
    state: toolCall.state,
    count: toolCall.count,
    serverLabel: "",
    labelKey: "agentic.chat.progress.running-tool",
    labelParams: { toolName: humanizeIdentifier(toolCall.name) },
    subSteps: [],
  };
}

function parseStatusStreamingEvent(data: unknown): AgenticToolCall | null {
  if (!isUnknownRecord(data)) {
    return null;
  }

  const phase = readString(data.phase);
  if (!phase) {
    return null;
  }

  switch (phase) {
    case "validated":
      return {
        id: "status:validated",
        name: phase,
        error: null,
        state: "done",
        count: 1,
        serverLabel: "",
        labelKey: "agentic.chat.progress.request-validated",
        subSteps: [],
      };
    case "openai_started":
      return {
        id: "status:ai-processing",
        name: phase,
        error: null,
        state: "running",
        count: 1,
        serverLabel: "",
        labelKey: "agentic.chat.progress.starting-ai-processing",
        subSteps: [],
      };
    case "openai_finished":
      return {
        id: "status:ai-processing",
        name: phase,
        error: null,
        state: "done",
        count: 1,
        serverLabel: "",
        labelKey: "agentic.chat.progress.ai-processing-finished",
        subSteps: [],
      };
    default:
      return null;
  }
}

function parseOpenAIStreamingEvent(data: unknown): AgenticToolCall | null {
  if (!isUnknownRecord(data)) {
    return null;
  }

  const type = readString(data.type);
  if (!type) {
    return null;
  }

  const toolName = readString(data.toolName);

  switch (type) {
    case "response.in_progress":
      return {
        id: "openai:response_state",
        name: type,
        error: null,
        state: "running",
        count: 1,
        serverLabel: "",
        labelKey: "agentic.chat.progress.analyzing-request",
        subSteps: [],
      };
    case "response.mcp_call.in_progress": {
      const label = toolName ? humanizeIdentifier(toolName) : "";
      return {
        id: "openai:mcp_call_state",
        name: type,
        error: null,
        state: "running",
        count: 1,
        serverLabel: label,
        labelKey: label ? undefined : "agentic.chat.progress.running-mcp-call",
        subSteps: [],
      };
    }
    case "response.mcp_call.completed": {
      const label = toolName ? humanizeIdentifier(toolName) : "";
      return {
        id: "openai:mcp_call_state",
        name: type,
        error: null,
        state: "done",
        count: 1,
        serverLabel: label,
        labelKey: label ? undefined : "agentic.chat.progress.mcp-call-completed",
        subSteps: [],
      };
    }
    case "response.completed":
      return {
        id: "openai:response_state",
        name: type,
        error: null,
        state: "done",
        count: 1,
        serverLabel: "",
        labelKey: "agentic.chat.progress.finalizing-response",
        subSteps: [],
      };
    default:
      return null;
  }
}

function parseToolProgress(data: unknown): AgenticToolCallSubStep | null {
  if (!isUnknownRecord(data)) {
    return null;
  }

  const step = readString(data.step);
  const message = readString(data.message);
  if (!step || !message) {
    return null;
  }

  return { step, message };
}

function humanizeIdentifier(value: string): string {
  const normalized = value
    .replace(/\.([a-zA-Z0-9])/g, " $1")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  if (!normalized) {
    return "Working";
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return !(typeof value !== "object" || value === null || Array.isArray(value));
}

function readString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function readStringPreserveWhitespace(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  return value;
}

function readNullableString(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }

  return readString(value);
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function readObject(value: unknown): Record<string, unknown> | undefined {
  if (isUnknownRecord(value)) {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    return isUnknownRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function readJsonValue(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
}

function parseTransactionType(
  value: unknown,
): TransactionType.ISSUE | TransactionType.BUY_SELL | undefined {
  const normalized = readString(value)
    ?.toUpperCase()
    .replace(/[\s\-/]/g, "_");

  if (normalized === TransactionType.ISSUE) {
    return TransactionType.ISSUE;
  }

  if (normalized === TransactionType.BUY_SELL || normalized === "BUYSELL") {
    return TransactionType.BUY_SELL;
  }

  return undefined;
}

function inferTransactionTypeFromPayload(
  value: unknown,
): TransactionType.ISSUE | TransactionType.BUY_SELL | undefined {
  const payload = readObject(value) ?? (isUnknownRecord(value) ? value : undefined);
  if (!payload) {
    return undefined;
  }

  if (Array.isArray(payload.shareAllocation)) {
    return TransactionType.ISSUE;
  }

  if (Array.isArray(payload.saleDetails) || Array.isArray(payload.sharetypeSellingPrices)) {
    return TransactionType.BUY_SELL;
  }

  return undefined;
}

function parseMessageDelta(data: unknown): string | undefined {
  if (!isUnknownRecord(data)) {
    return undefined;
  }

  return readStringPreserveWhitespace(data.delta);
}

function parseMessageResponse(data: unknown): string | undefined {
  if (!isUnknownRecord(data)) {
    return undefined;
  }

  return readStringPreserveWhitespace(data.response);
}

function parseErrorMessage(data: unknown): string | undefined {
  if (!isUnknownRecord(data)) {
    return undefined;
  }

  return readString(data.message);
}

function readDraftFields(value: unknown): {
  payload?: unknown;
  transactionType?: unknown;
} {
  const data = readObject(value);
  if (!data) {
    return {};
  }

  return {
    payload: "payload" in data ? data.payload : undefined,
    transactionType: data.transactionType,
  };
}

function readDraftFieldsFromOutput(output: unknown): {
  payload?: unknown;
  transactionType?: unknown;
} {
  const normalizedOutput = readJsonValue(output);
  const direct = readDraftFields(normalizedOutput);
  const outputObject = readObject(normalizedOutput);
  const contentItems = Array.isArray(normalizedOutput)
    ? normalizedOutput
    : Array.isArray(outputObject?.content)
      ? outputObject.content
      : [];

  for (const item of contentItems) {
    const normalizedItem = readJsonValue(item);
    const itemObject = readObject(normalizedItem);
    const fields = [
      readDraftFields(normalizedItem),
      readDraftFields(itemObject),
      readDraftFields(itemObject?.json),
      readDraftFields(itemObject?.text),
      readDraftFields(itemObject?.data),
    ];

    for (const field of fields) {
      if (field.payload !== undefined || field.transactionType !== undefined) {
        return {
          payload: direct.payload ?? field.payload,
          transactionType: direct.transactionType ?? field.transactionType,
        };
      }
    }
  }

  return direct;
}

function parseToolCall(data: unknown): AgenticToolCall | null {
  if (!isUnknownRecord(data)) {
    return null;
  }

  const id = readString(data.id);
  const name = readString(data.name);
  if (!id || !name) {
    return null;
  }

  const error = data.error === null ? null : (readString(data.error) ?? null);
  const state: AgenticProgressState = error ? "error" : "done";

  return {
    id,
    name,
    error,
    state,
    count: 1,
    serverLabel: readString(data.serverLabel) ?? "",
    subSteps: [],
  };
}

function parseIssueShareAllocation(
  value: unknown,
  defaultCurrency: string,
): IssueShareTypeDTO | undefined {
  if (!isUnknownRecord(value)) {
    return undefined;
  }

  const sharetypeId = readNumber(value.sharetypeId);
  const shareholderId = readNumber(value.shareholderId);
  const numberOfShares = readNumber(value.numberOfShares);

  if (sharetypeId === undefined || shareholderId === undefined) {
    return undefined;
  }

  if (numberOfShares === undefined) {
    return undefined;
  }

  return {
    sharetypeId,
    shareholderId,
    numberOfShares,
    sharePrice: readNumber(value.sharePrice) ?? 0,
    currency: readString(value.currency) ?? defaultCurrency,
  };
}

function parseIssueDraftPayload(
  value: unknown,
  organizationnumber: string,
): AgenticIssueDraftPayload | null {
  if (value === null) {
    return null;
  }

  if (!isUnknownRecord(value)) {
    return null;
  }

  const defaultCurrency = readString(value.currency) ?? "NOK";
  const shareAllocation = (Array.isArray(value.shareAllocation) ? value.shareAllocation : [])
    .map((allocation) => parseIssueShareAllocation(allocation, defaultCurrency))
    .filter((allocation): allocation is IssueShareTypeDTO => allocation !== undefined);

  if (shareAllocation.length === 0) {
    return null;
  }

  const payload: AgenticIssueDraftPayload = {
    organizationnumber,
    transactionType: TransactionType.ISSUE,
    shareAllocation,
    isBonusIssue: readBoolean(value.isBonusIssue) ?? false,
    boardMeetingPoDDate: readNullableString(value.boardMeetingPoDDate) ?? null,
    generalMeetingDate: readNullableString(value.generalMeetingDate) ?? null,
    registeredInNorwegian: readBoolean(value.registeredInNorwegian) ?? false,
    isPending: readBoolean(value.isPending) ?? false,
  };

  const registrationDate = readString(value.registrationDate);
  if (registrationDate) {
    payload.registrationDate = registrationDate;
  }

  const shareholderRightsDate = readString(value.shareholderRightsDate);
  if (shareholderRightsDate) {
    payload.shareholderRightsDate = shareholderRightsDate;
  }

  const issuedBy = readString(value.issuedBy);
  if (issuedBy) {
    payload.issuedBy = issuedBy as IssueNewSharesDTO["issuedBy"];
  }

  const incorporationDate = readString(value.incorporationDate);
  if (incorporationDate) {
    payload.incorporationDate = incorporationDate;
  }

  const increaseType = readString(value.increaseType);
  if (increaseType) {
    payload.increaseType = increaseType as IssueNewSharesDTO["increaseType"];
  }

  const shareNominalValue = readNumber(value.shareNominalValue);
  if (shareNominalValue !== undefined) {
    payload.shareNominalValue = shareNominalValue;
  }

  return payload;
}

function parseBuyDetails(
  value: unknown,
  fallbackCurrency: string,
): NewTransaction["saleDetails"][number] | undefined {
  if (!isUnknownRecord(value)) {
    return undefined;
  }

  const buyerShareholderId = readNumber(value.buyerShareholderId ?? value.buyerId);
  const shareTypeId = readNumber(value.shareTypeId ?? value.sharetypeId);
  const numberOfShares = readNumber(value.numberOfShares);
  const sellingPriceValue = value.sellingPrice;
  const sellingPrice =
    sellingPriceValue === null || sellingPriceValue === undefined
      ? null
      : readNumber(sellingPriceValue);
  const currency = readString(value.currency) ?? fallbackCurrency;
  const taxContinuity = readBoolean(value.taxContinuity) ?? false;

  if (
    buyerShareholderId === undefined ||
    shareTypeId === undefined ||
    numberOfShares === undefined ||
    (sellingPriceValue !== null && sellingPriceValue !== undefined && sellingPrice === undefined)
  ) {
    return undefined;
  }

  return {
    buyerShareholderId,
    shareTypeId,
    numberOfShares,
    sellingPrice: sellingPrice ?? null,
    currency,
    buyerBrokerFee: readNumber(value.buyerBrokerFee),
    buyerOtherCosts: readNumber(value.buyerOtherCosts),
    sellerBrokerFee: readNumber(value.sellerBrokerFee),
    sellerOtherCosts: readNumber(value.sellerOtherCosts),
    taxContinuity,
  };
}

function parseNewTransactionPayload(
  value: unknown,
  organizationnumber: string,
): NewTransaction | undefined {
  if (!isUnknownRecord(value)) {
    return undefined;
  }

  const fallbackCurrency = readString(value.currency) ?? "NOK";
  const sellerShareholderId = readNumber(value.sellerShareholderId ?? value.sellerId);
  const transactionDate = readString(value.transactionDate);
  const transactionType = value.transactionType
    ? parseTransactionType(value.transactionType)
    : TransactionType.BUY_SELL;
  const saleDetails = (Array.isArray(value.saleDetails) ? value.saleDetails : [])
    .map((detail) => parseBuyDetails(detail, fallbackCurrency))
    .filter((detail): detail is NewTransaction["saleDetails"][number] => detail !== undefined);

  if (
    !organizationnumber ||
    sellerShareholderId === undefined ||
    !transactionDate ||
    transactionType !== TransactionType.BUY_SELL ||
    saleDetails.length === 0
  ) {
    return undefined;
  }

  const payload: NewTransaction = {
    organizationnumber,
    sellerShareholderId,
    saleDetails,
    transactionDate,
    transactionType: TransactionType.BUY_SELL,
  };

  const comment = readString(value.comment);
  if (comment) {
    payload.comment = comment;
  }

  return payload;
}

function parseSharetypeSellingPrice(
  value: unknown,
  fallbackCurrency: string,
): TransactionAllShares["sharetypeSellingPrices"][number] | undefined {
  if (!isUnknownRecord(value)) {
    return undefined;
  }

  const sellingPrice = readNumber(value.sellingPrice);
  const currency = readString(value.currency) ?? fallbackCurrency;
  const sharetypeId = readNumber(value.sharetypeId);

  if (sellingPrice === undefined || sharetypeId === undefined) {
    return undefined;
  }

  return {
    sellingPrice,
    currency,
    sharetypeId,
    sellerBrokerFee: readNumber(value.sellerBrokerFee),
    buyerBrokerFee: readNumber(value.buyerBrokerFee),
  };
}

function parseTransactionAllSharesPayload(
  value: unknown,
  organizationnumber: string,
): TransactionAllShares | undefined {
  if (!isUnknownRecord(value)) {
    return undefined;
  }

  const transactionDate = readString(value.transactionDate);
  const buyerShareholderId = readNumber(value.buyerShareholderId);
  const includeCompanyOwnShares = readBoolean(value.includeCompanyOwnShares) ?? false;
  const fallbackCurrency = readString(value.currency) ?? "NOK";
  const sharetypeSellingPrices = (
    Array.isArray(value.sharetypeSellingPrices) ? value.sharetypeSellingPrices : []
  )
    .map((price) => parseSharetypeSellingPrice(price, fallbackCurrency))
    .filter(
      (price): price is TransactionAllShares["sharetypeSellingPrices"][number] =>
        price !== undefined,
    );

  if (
    !organizationnumber ||
    !transactionDate ||
    buyerShareholderId === undefined ||
    sharetypeSellingPrices.length === 0
  ) {
    return undefined;
  }

  const payload: TransactionAllShares = {
    organizationnumber,
    transactionDate,
    buyerShareholderId,
    includeCompanyOwnShares,
    sharetypeSellingPrices,
  };

  const brokerFee = readNumber(value.brokerFee);
  if (brokerFee !== undefined) {
    payload.brokerFee = brokerFee;
  }

  const otherCosts = readNumber(value.otherCosts);
  if (otherCosts !== undefined) {
    payload.otherCosts = otherCosts;
  }

  const sellerOtherCosts = readNumber(value.sellerOtherCosts);
  if (sellerOtherCosts !== undefined) {
    payload.sellerOtherCosts = sellerOtherCosts;
  }

  const comment = readString(value.comment);
  if (comment) {
    payload.comment = comment;
  }

  return payload;
}

function parseBuySellDraftPayload(
  value: unknown,
  organizationnumber: string,
): NewTransaction | TransactionAllShares | null {
  if (value === null) {
    return null;
  }

  return (
    parseNewTransactionPayload(value, organizationnumber) ??
    parseTransactionAllSharesPayload(value, organizationnumber) ??
    null
  );
}

export function parseAgenticDraftTransaction(
  data: unknown,
  orgNo?: string,
): AgenticDraftTransaction | null {
  const organizationnumber = readString(orgNo);
  if (!organizationnumber) {
    return null;
  }

  if (!isUnknownRecord(data)) {
    return null;
  }

  const directFields = readDraftFields(data);
  const argumentFields = readDraftFields(data.arguments);
  const resultFields = readDraftFieldsFromOutput(data.result);
  const hasPayloadField = "payload" in data;

  if (!hasPayloadField && argumentFields.payload === undefined) {
    return null;
  }

  const payload = hasPayloadField
    ? (directFields.payload ?? argumentFields.payload ?? resultFields.payload)
    : argumentFields.payload;
  if (payload === undefined) {
    return null;
  }

  const payloadObject = readObject(payload);
  const transactionType =
    parseTransactionType(data.transactionType) ||
    parseTransactionType(directFields.transactionType) ||
    parseTransactionType(argumentFields.transactionType) ||
    parseTransactionType(resultFields.transactionType) ||
    parseTransactionType(payloadObject?.transactionType) ||
    inferTransactionTypeFromPayload(payload);
  if (!transactionType) {
    return null;
  }

  const base = {
    source: readString(data.source) ?? "draftTransaction",
    toolCallId: readString(data.toolCallId) ?? readString(data.id) ?? "",
    rawPayload: payload,
    error: data.error === null ? null : (readString(data.error) ?? null),
  };

  if (transactionType === TransactionType.ISSUE) {
    return {
      ...base,
      transactionType,
      payload: parseIssueDraftPayload(payload, organizationnumber),
    };
  }

  return {
    ...base,
    transactionType,
    payload: parseBuySellDraftPayload(payload, organizationnumber),
  };
}

export function parseAgenticDraftTransactionFromToolCall(
  data: unknown,
  orgNo?: string,
): AgenticDraftTransaction | null {
  if (!isUnknownRecord(data)) {
    return null;
  }

  const toolName = readString(data.name);
  if (toolName !== "draftTransaction" && toolName !== "registerTransaction") {
    return null;
  }

  const argumentsObject = readObject(data.arguments);
  const argumentFields = readDraftFields(argumentsObject);
  const outputFields = readDraftFieldsFromOutput(data.output);

  const payload = argumentFields.payload ?? outputFields.payload;
  if (payload === undefined) {
    return null;
  }

  const payloadObject = readObject(payload);
  const transactionType =
    parseTransactionType(argumentFields.transactionType) ||
    parseTransactionType(outputFields.transactionType) ||
    parseTransactionType(payloadObject?.transactionType) ||
    inferTransactionTypeFromPayload(payload);

  if (!transactionType) {
    return null;
  }

  return parseAgenticDraftTransaction(
    {
      source: "tool_call",
      toolCallId: readString(data.id) ?? "",
      transactionType,
      payload,
      error: data.error,
    },
    orgNo,
  );
}

function fileToAttachment(file: File): Promise<AgenticAskAttachment> {
  return readFileAsDataUrl(file).then((dataUrl) => ({
    filename: file.name,
    mediaType: guessMediaType(file),
    dataUrl,
  }));
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Invalid file data"));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function guessMediaType(file: File): string {
  if (file.type) {
    return file.type;
  }

  const filename = file.name.toLowerCase();
  if (filename.endsWith(".pdf")) {
    return "application/pdf";
  }
  if (filename.endsWith(".png")) {
    return "image/png";
  }
  return "application/octet-stream";
}
